import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type QueryState,
  closeModal,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { Assessment, FunctionalTest } from '../type';

import { trimValue } from 'helpers';
import { setUrlParams } from 'helpers/url';
import { useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { useUpdateQueryState } from 'hooks/url';
import { cloneDeep, debounce, isUndefined } from 'lodash';

import { SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useFunctionalTestController = () => {
  const navigationTo = useNavigate();

  const params = useParams();

  const { institutionId } = params;

  useUpdateQueryState(TABLE_KEY.FUNCTIONAL_TEST, 12);

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.FUNCTIONAL_TEST]);

  const userProfile = useAuthStore(userProfileSelector);

  const [functionalTestData, setFunctionTestData] = useState<FunctionalTest[]>([]);

  const total = useNumber(0);

  const searchValue = useString('');

  const functionalTestSelectedId = useString();

  const [selectedFunctionalTest, setSelectedFunctionalTest] = useState<Record<string, string>>({});

  const [assessmentData, setAssessmentData] = useState<Assessment[]>([]);

  const searchAssessment = useString('');

  useEffect(() => {
    if (!userProfile?.id) {
      return;
    }

    if (queryState) {
      setUrlParams(queryState);

      searchValue.setValue(queryState.search ?? '');

      getListFunctionalTest();
    }
  }, [JSON.stringify(queryState), userProfile]);

  useEffect(() => {
    getListAssessment();
  }, []);

  const getListFunctionalTest = async (search: string = '') => {
    const result: {
      items: FunctionalTest[];
      total: number;
    } = await apiCall({
      url: `/functional-tests`,
      method: 'GET',
      data: {
        ...queryState,
        institutionId: userProfile?.institution?.id ?? institutionId,
      },
      isLoading: true,
      showError: true,
    });

    if (result?.items) {
      setFunctionTestData(result?.items ?? []);
    }

    if (result?.total) {
      total.setValue(result.total);
    }
  };

  const getListAssessment = async (search: string = '') => {
    const result: {
      items: Assessment[];
      total: number;
    } = await apiCall({
      url: '/assessments',
      method: 'GET',
      isLoading: true,
      data: {
        page: 1,
        limit: 10,
        institutionId: userProfile?.institution?.id ?? institutionId,
        search: search,
      },
    });

    if (result?.items) {
      setAssessmentData(result?.items ?? []);
    }
  };

  const updateQueryState = (key: string, newState: QueryState) => {
    updateQueryStateTable(key, newState);
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.FUNCTIONAL_TEST, {
        ...queryState,
        search: newSearch,
        page: 1,
      });
    }, 500),
    [],
  );

  const onChangeSelectedFunctionalTest = (functionalTest: string) => () => {
    const newSelected = cloneDeep(selectedFunctionalTest);

    if (!isUndefined(newSelected[functionalTest])) {
      delete newSelected[functionalTest];
    } else {
      newSelected[functionalTest] = functionalTest;
    }

    setSelectedFunctionalTest(newSelected);
  };

  const goToFunctionalTestDetail = (functionalTestId: string) => {
    navigationTo(PATH.EDIT_FUNCTIONAL_TESTS.replace(':id', functionalTestId));
  };

  const handleEditFunctionalTest = (functionalTestId: string) => async () => {
    navigationTo(PATH.EDIT_FUNCTIONAL_TESTS.replace(':id', functionalTestId));
  };

  const handleDeleteFunctionalTest = (functionalTestId: string) => async () => {
    openModal(MODAL_KEY.DELETE_FUNCTIONAL_TEST);
    functionalTestSelectedId.setValue(functionalTestId);
  };

  const callbackFirstPage = () => {
    updateQueryStateTable(TABLE_KEY.FUNCTIONAL_TEST, {
      ...queryState,
      page: 1,
    });
  };

  const handleDuplicateFunctionalTest = (functionalTestId: string) => async () => {
    const result: FunctionalTest = await apiCall({
      url: `/functional-tests/${functionalTestId}/duplicate`,
      method: 'POST',
      isLoading: true,
      showError: true,
    });

    if (result?.id) {
      showToastMessage('success', 'Functional test duplicated successfully!');
      getListFunctionalTest();
    }
  };

  const onDeleteFunctionalTest = async () => {
    const responsive: {
      statusCode: number;
    } = await apiCall({
      url: `/functional-tests/${functionalTestSelectedId.value}`,
      method: 'DELETE',
      isLoading: true,
      showError: true,
    });

    closeModal(MODAL_KEY.DELETE_FUNCTIONAL_TEST);

    if (responsive.statusCode === 400) {
      return;
    }

    if (queryState.page === 1) {
      getListFunctionalTest();
    }

    callbackFirstPage();

    setSelectedFunctionalTest({});
    showToastMessage('success', SUCCESS_MESSAGE.DELETE_FUNCTIONAL_TEST);
  };

  const handleDelete = async () => {
    const result: string[] = [];

    for (const el of Object.keys(selectedFunctionalTest)) {
      const response: { id: string } = await apiCall({
        url: `/functional-tests/${selectedFunctionalTest[el]}`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      if (response?.id) {
        result.push(response.id);
      }
    }

    if (result.length === Object.keys(selectedFunctionalTest).length) {
      callbackFirstPage();
      showToastMessage('success', SUCCESS_MESSAGE.DELETE_FUNCTIONAL_TEST);
    }

    setSelectedFunctionalTest({});
    getListFunctionalTest();
  };

  const handleAddFunctionalTestToAssessment = async (assessmentId: string) => {
    for (const el of Object.keys(selectedFunctionalTest)) {
      await apiCall({
        url: '/assessments-functional-tests',
        method: 'POST',
        data: {
          functionalTestId: selectedFunctionalTest[el],
          assessmentId,
        },
        isLoading: true,
        showError: true,
      });
    }

    showToastMessage('success', SUCCESS_MESSAGE.ADD_FUNCTIONAL_TEST_TO_ASSESSMENT);
  };

  const onChangeSearchAssessment = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchAssessment.setValue(event.target.value);
    debounceSearchAssessment(event.target.value);
  };

  const debounceSearchAssessment = useCallback(
    debounce((newSearch: string) => {
      getListAssessment(trimValue(newSearch));
    }, 500),
    [],
  );

  return {
    functionalTestData,
    total,
    queryState,
    updateQueryState,
    onChangeSearchValue,
    searchValue,
    goToFunctionalTestDetail,
    handleEditFunctionalTest,
    handleDeleteFunctionalTest,
    handleDuplicateFunctionalTest,
    onChangeSelectedFunctionalTest,
    setSelectedFunctionalTest,
    selectedFunctionalTest,
    onDeleteFunctionalTest,
    handleDelete,
    handleAddFunctionalTestToAssessment,
    assessmentData,
    searchAssessment,
    onChangeSearchAssessment,
  };
};
